import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../services/adminService';

function AdminSettings() {
  const [settings, setSettings] = useState({
    retention_period: 30,
    auto_cleanup_enabled: true,
    last_cleanup_date: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSettings();
      setSettings(response.data);
    } catch (err) {
      setError('Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'retention_period' ? parseInt(value) : value)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      await adminService.updateSettings({
        retention_period: settings.retention_period,
        auto_cleanup_enabled: settings.auto_cleanup_enabled
      });
      
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="container">
        <div className="page-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1>System Settings</h1>
              <p>Configure audit logs and file retention policies</p>
            </div>
            <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message" style={{ 
          background: '#d4edda', 
          color: '#155724', 
          padding: '12px 20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>{success}</div>}

        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
          <div className="stat-card">
            <div className="stat-label">Retention Period</div>
            <div className="stat-value">{settings.retention_period} days</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Auto Cleanup</div>
            <div className="stat-value" style={{ color: settings.auto_cleanup_enabled ? '#28a745' : '#dc3545' }}>
              {settings.auto_cleanup_enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Last Cleanup</div>
            <div className="stat-value" style={{ fontSize: '18px' }}>
              {formatDate(settings.last_cleanup_date)}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Retention Settings</div>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label htmlFor="retention_period">Retention Period</label>
              <select
                id="retention_period"
                name="retention_period"
                className="form-control"
                value={settings.retention_period}
                onChange={handleChange}
                style={{ maxWidth: '300px' }}
              >
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
              <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                Audit logs and uploaded files older than this will be automatically deleted.
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="auto_cleanup_enabled"
                  checked={settings.auto_cleanup_enabled}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Enable automatic cleanup</span>
              </label>
              <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                When enabled, a cleanup job runs daily at 2:00 AM to remove old logs and files.
              </small>
            </div>

            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>

        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">Quick Links</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <Link to="/admin/audit-logs" className="btn btn-secondary" style={{ textAlign: 'center' }}>
              View Audit Logs
            </Link>
            <Link to="/admin/files" className="btn btn-secondary" style={{ textAlign: 'center' }}>
              View Uploaded Files
            </Link>
            <Link to="/admin" className="btn btn-secondary" style={{ textAlign: 'center' }}>
              User Management
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
